import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { IngredientDto, IngredientSummary } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { IngredientAutocomplete } from './IngredientAutocomplete';

/** Admin-only: fold this ingredient into another one everywhere it's referenced. */
export function MergeDialog({
  ingredient,
  open,
  onClose,
}: {
  ingredient: IngredientDto;
  open: boolean;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [target, setTarget] = useState<IngredientSummary | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const merge = useMutation({
    mutationFn: () => api.ingredients.merge(ingredient.id, { intoId: target!.id }),
    onSuccess: (merged) => {
      void qc.invalidateQueries({ queryKey: ['ingredients'] });
      onClose();
      void navigate(`/ingredients/${merged.id}`, { replace: true });
    },
  });

  return (
    <Dialog open={open} onClose={onClose} title={`Merge “${ingredient.canonicalName}”`}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted">
          Every pantry item, recipe, grocery list line and photo that uses “
          {ingredient.canonicalName}” moves to the ingredient you pick. This can’t be undone.
        </p>
        <IngredientAutocomplete
          id="ingredient-merge-target"
          label="Merge into"
          value={text}
          onChange={(t) => {
            setText(t);
            setTarget(null);
          }}
          onSelect={(ing) => {
            setTarget(ing);
            setText(ing.canonicalName);
          }}
          excludeId={ingredient.id}
          placeholder="Search for the ingredient to keep…"
        />
        {merge.isError ? <Alert tone="error">{messageForError(merge.error)}</Alert> : null}
        <div className="flex gap-2">
          <Button
            variant="danger"
            disabled={!target || merge.isPending}
            onClick={() => {
              if (
                target &&
                confirm(`Merge "${ingredient.canonicalName}" into "${target.canonicalName}"?`)
              ) {
                merge.mutate();
              }
            }}
          >
            {merge.isPending ? 'Merging…' : 'Merge'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={merge.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
